import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterLink} from '@angular/router';
import {NgxDatatableModule} from '@swimlane/ngx-datatable';
import {ToastrService} from 'ngx-toastr';
import {BreadcrumbService} from '@cdss/services/breadcrumb.service';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {OverlaySpinnerComponent} from '@shared/components/overlay-spinner/overlay-spinner.component';
import {OrganisationsService} from '@shared/api/services/v2/organisations.service';
import {isAlreadyNotified} from '@shared/api/services/v2/api-request';
import {
    OrganisationListFilters,
    OrganisationListItem,
    isOrganisationDeactivated,
    isOrganisationDeleted
} from '@shared/api/models/query/organisation.model';
import {Pagination, PaginationResponse} from '@models/pagination.model';
import {Permissions} from '@models/permissions.enum';
import {PermissionRolesMap} from '@models/permissions.db';
import {PermissionPipe} from '@components/directives/permission.pipe';
import {AuthService} from '@shared/services/security/auth.service';
import {ORGANISATION_NEW_ROUTE, organisationDetailRoute} from '@shared/services/router/organisation-routes';
import {OrganisationsFilterComponent} from './filter/organisations-filter.component';
import {OrganisationsFilterDto} from './filter/organisations-filter.dto';

/**
 * UNIVAPLAT-3691: admin organisations list (route `cdss/admin/organisations`, VIEW_ORGANISATIONS).
 * General admin sees "All organisations", an organisation admin "My organisations" (the API
 * scopes the data; the UI only hides what that role cannot use). Nothing from the response is
 * logged or stored, and the rows are dropped when the page is destroyed.
 */
@Component({
    selector: 'cdss-organisations-list-page',
    imports: [
        CommonModule,
        RouterLink,
        NgxDatatableModule,
        SpinnerComponent,
        OverlaySpinnerComponent,
        OrganisationsFilterComponent,
        PermissionPipe,
    ],
    standalone: true,
    templateUrl: './organisations-list.page.html',
    styleUrls: ['./organisations-list.page.scss']
})
export class OrganisationsListPage implements OnInit, OnDestroy {
    readonly Permissions = Permissions;
    readonly newOrganisationRoute = '/' + ORGANISATION_NEW_ROUTE;

    loading = true;
    refreshing = false;

    pagination: Pagination = new Pagination(25);
    rows: OrganisationListItem[] = [];

    filter: OrganisationsFilterDto = new OrganisationsFilterDto();
    showFilters = false;

    @ViewChild('filtersButton') filtersButton?: ElementRef<HTMLButtonElement>;

    /** Guards against an older, slower response overwriting a newer one. */
    private loadSeq = 0;

    constructor(
        private breadcrumbService: BreadcrumbService,
        private toastr: ToastrService,
        private organisationsService: OrganisationsService,
        private authService: AuthService,
    ) {
    }

    /** General admin signal: only they hold CREATE_ORGANISATION. */
    get isGeneralAdmin(): boolean {
        const userRoles = this.authService.roles ?? [];
        return (PermissionRolesMap[Permissions.CREATE_ORGANISATION] ?? []).some(role => userRoles.includes(role));
    }

    get heading(): string {
        return this.isGeneralAdmin ? 'All organisations' : 'My organisations';
    }

    ngOnInit(): void {
        this.breadcrumbService.set([
            {title: 'Organisations', url: null}
        ]);
        this.refreshRows();
    }

    ngOnDestroy(): void {
        // Contract titles are the only linked data here, but drop everything with the page.
        this.loadSeq++;
        this.rows = [];
    }

    detailRoute(row: OrganisationListItem): string {
        return '/' + organisationDetailRoute(row.id);
    }

    contractTitles(row: OrganisationListItem): string {
        const titles = (row.contracts ?? []).map(contract => contract.title).filter(title => !!title);
        return titles.length ? titles.join(', ') : '-';
    }

    statusLabel(row: OrganisationListItem): string | null {
        if (isOrganisationDeleted(row)) {
            return 'Deleted';
        }
        return isOrganisationDeactivated(row) ? 'Deactivated' : null;
    }

    toggleFilters(): void {
        this.showFilters = !this.showFilters;
        if (!this.showFilters) {
            this.restoreFocus();
        }
    }

    onFilterApply(): void {
        this.refreshing = true;
        this.pagination.start = 0;
        this.refreshRows();
        this.showFilters = false;
        this.restoreFocus();
    }

    onFilterCancel(): void {
        this.showFilters = false;
        this.restoreFocus();
    }

    setPaginationSize(size: number): void {
        this.refreshing = true;
        this.pagination.size = size;
        this.pagination.start = 0;
        this.refreshRows();
    }

    previousPage(): void {
        this.refreshing = true;
        this.pagination.start = this.pagination.currentPage - 2;
        this.refreshRows();
    }

    nextPage(): void {
        this.refreshing = true;
        this.pagination.start = this.pagination.currentPage;
        this.refreshRows();
    }

    async refreshRows(): Promise<void> {
        const seq = ++this.loadSeq;
        try {
            const response: PaginationResponse<OrganisationListItem> = await this.organisationsService.list(this.pagination, this.buildFilters());
            if (seq !== this.loadSeq) {
                return;
            }
            const hasData = !!response?.data?.length;
            this.rows = response?.data || [];
            this.pagination.totalCount = hasData ? response.pagination.totalCount : 0;
            this.pagination.fromRecord = hasData ? response.pagination.fromRecord : 0;
            this.pagination.toRecord = hasData ? response.pagination.toRecord : 0;
            this.pagination.currentPage = hasData ? response.pagination.currentPage : 0;
            this.pagination.totalPages = hasData ? response.pagination.totalPages : 0;
        } catch (error) {
            if (seq !== this.loadSeq) {
                return;
            }
            // Backend owns the error text; nothing from the response is logged.
            if (!isAlreadyNotified(error)) {
                this.toastr.error('Failed to load organisations');
            }
        } finally {
            if (seq === this.loadSeq) {
                this.loading = false;
                this.refreshing = false;
            }
        }
    }

    private buildFilters(): OrganisationListFilters {
        const filters: OrganisationListFilters = {};
        if (this.filter.name) filters.name = this.filter.name;
        if (this.filter.code) filters.code = this.filter.code;
        if (this.filter.address) filters.address = this.filter.address;
        if (this.filter.city) filters.city = this.filter.city;
        if (this.filter.postcode) filters.postcode = this.filter.postcode;
        if (this.isGeneralAdmin) {
            if (this.filter.showDeactivated) filters.showDeactivated = true;
            if (this.filter.showDeleted) filters.showDeleted = true;
        }
        return filters;
    }

    /** Return focus to the Filters button once the overlay is gone. */
    private restoreFocus(): void {
        setTimeout(() => this.filtersButton?.nativeElement.focus());
    }
}
